import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { Memberships } from "../../types/Types";
import {
  EARLY_STAGE_MEMBERSHIP,
  isEarlyStageMembership,
  isLegacyYouthMembership,
} from "../../utils/membershipTier";

/** Persist selected plan on the user row only after payment (hasPaid=1). Prefer not calling before checkout. */
export function buildRegistrationMembershipSyncFormData(options: {
  objectId: string;
  membershipName: string;
  membershipId?: number;
  isAnnualMembership: number;
}): FormData {
  const formData = new FormData();
  formData.append("objectId", options.objectId.trim());
  formData.append("membershipName", options.membershipName.trim());
  if (options.membershipId != null && options.membershipId > 0) {
    formData.append("membershipId", String(options.membershipId));
  }
  formData.append("isAnnualMembership", String(options.isAnnualMembership));
  return formData;
}

/** Match membership catalog row by name (exact, case-insensitive, Early Stage / Youth aliases). */
export function findMembershipByName(
  memberships: Memberships[],
  membershipName: string,
): Memberships | undefined {
  const want = membershipName.trim();
  if (!want) return undefined;

  const exact = memberships.find((m) => m.name === want);
  if (exact) return exact;

  const lower = want.toLowerCase();
  const ci = memberships.find((m) => m.name.trim().toLowerCase() === lower);
  if (ci) return ci;

  if (isEarlyStageMembership(want)) {
    return memberships.find((m) => isEarlyStageMembership(m.name));
  }
  if (isLegacyYouthMembership(want)) {
    return memberships.find((m) => isLegacyYouthMembership(m.name));
  }
  return undefined;
}

export function resolveMembershipIdForName(
  memberships: Memberships[],
  membershipName: string,
): number | undefined {
  const match = findMembershipByName(memberships, membershipName);
  return match?.id && match.id > 0 ? match.id : undefined;
}

/** Checkout may run before catalog includes Early Stage; fall back to resolved Stripe product. */
export function findMembershipForCheckout(
  memberships: Memberships[],
  membershipName: string,
  options?: {
    earlyStageProductId?: string;
    earlyStageMonthlyFeeGbp?: number;
  },
): Memberships | undefined {
  const found = findMembershipByName(memberships, membershipName);
  if (found) return found;

  const productId = options?.earlyStageProductId?.trim();
  if (isEarlyStageMembership(membershipName) && productId) {
    return {
      id: 0,
      name: membershipName.trim() || EARLY_STAGE_MEMBERSHIP,
      monthlyFee: String(options?.earlyStageMonthlyFeeGbp ?? 0),
      annualFee: "0",
      productId,
      productIdAnnual: "",
    };
  }
  return undefined;
}

/** Saves membership selection only — backend ignores Membership* until hasPaid=1 for unpaid users. */
export async function syncRegistrationMembership(options: {
  objectId: string;
  membershipName: string;
  membershipId?: number;
  isAnnualMembership: number;
}): Promise<void> {
  const formData = buildRegistrationMembershipSyncFormData(options);
  const config = { headers: { "Content-Type": "multipart/form-data" } };

  let hasId = formData.get("id");
  if (!hasId) {
    const existing = await axiosInstance.get(
      getAPIURL(`/User/ByObjectId/${encodeURIComponent(options.objectId.trim())}`),
    );
    const existingId =
      existing?.data?.user?.id ?? existing?.data?.User?.id ?? null;
    if (existingId) {
      hasId = String(existingId);
      formData.set("id", hasId);
    }
  }

  if (hasId) {
    await axiosInstance.put(getAPIURL("/User/Update"), formData, config);
  } else {
    await axiosInstance.post(getAPIURL("/User/Create"), formData, config);
  }
}

export type RegistrationPersonalInfo = {
  objectId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  city?: string;
  country?: string;
  membershipName?: string;
  membershipId?: number;
  isAnnualMembership?: number;
  personalPositionCompany?: string;
  businessName?: string;
  billingAddress?: string;
  referralCodeUsed?: string;
};

/** Saves personal details from step 1 (does not mark paid). */
export async function syncRegistrationPersonalInfo(
  info: RegistrationPersonalInfo,
): Promise<void> {
  let email = info.email.trim();
  if (!email) {
    try {
      email = localStorage.getItem("auth_email")?.trim() ?? "";
    } catch {
      /* ignore */
    }
  }

  const formData = new FormData();
  formData.append("objectId", info.objectId.trim());
  formData.append("firstName", info.firstName.trim());
  formData.append("lastName", info.lastName.trim());
  formData.append("email", email);
  if (info.phoneNumber) formData.append("phoneNumber", info.phoneNumber.trim());
  if (info.city) formData.append("city", info.city.trim());
  if (info.country) formData.append("country", info.country.trim());
  if (info.membershipName?.trim()) {
    formData.append("membershipName", info.membershipName.trim());
  }
  if (info.membershipId != null && info.membershipId > 0) {
    formData.append("membershipId", String(info.membershipId));
  }
  if (info.isAnnualMembership != null) {
    formData.append("isAnnualMembership", String(info.isAnnualMembership));
  }
  if (info.personalPositionCompany) {
    formData.append("personalPositionCompany", info.personalPositionCompany.trim());
  }
  if (info.businessName) formData.append("businessName", info.businessName.trim());
  if (info.billingAddress) {
    formData.append("billingAddress", info.billingAddress.trim());
  }
  if (info.referralCodeUsed) {
    formData.append("referralCodeUsed", info.referralCodeUsed.trim());
  }

  const config = { headers: { "Content-Type": "multipart/form-data" } };
  let hasId = formData.get("id");
  if (!hasId) {
    const existing = await axiosInstance.get(
      getAPIURL(`/User/ByObjectId/${encodeURIComponent(info.objectId.trim())}`),
    );
    const existingId =
      existing?.data?.user?.id ?? existing?.data?.User?.id ?? null;
    if (existingId) {
      formData.set("id", String(existingId));
      hasId = formData.get("id");
    }
  }

  if (hasId) {
    await axiosInstance.put(getAPIURL("/User/Update"), formData, config);
  } else {
    await axiosInstance.post(getAPIURL("/User/Create"), formData, config);
  }
}

export async function syncRegistrationTerms(options: {
  objectId: string;
  isTermConditions: number;
  isDirectory: number;
  /** Step 3 = pre-checkout; triggers unpaid signup webhook on the API. */
  registrationStep?: number;
}): Promise<void> {
  const formData = new FormData();
  formData.append("objectId", options.objectId.trim());
  formData.append("isTermConditions", String(options.isTermConditions));
  formData.append("isDirectory", String(options.isDirectory));
  if (options.registrationStep != null) {
    formData.append("registrationStep", String(options.registrationStep));
  }

  const config = { headers: { "Content-Type": "multipart/form-data" } };
  const existing = await axiosInstance.get(
    getAPIURL(`/User/ByObjectId/${encodeURIComponent(options.objectId.trim())}`),
  );
  const existingId =
    existing?.data?.user?.id ?? existing?.data?.User?.id ?? null;
  if (!existingId) {
    throw new Error("User profile not found");
  }
  formData.set("id", String(existingId));
  await axiosInstance.put(getAPIURL("/User/Update"), formData, config);
}

/** Triggers unpaid signup webhook with current DB profile (registration step 3). */
export async function syncRegistrationUnpaidSignupStep(options: {
  objectId: string;
}): Promise<void> {
  const formData = new FormData();
  formData.append("objectId", options.objectId.trim());
  formData.append("registrationStep", "3");

  const config = { headers: { "Content-Type": "multipart/form-data" } };
  const existing = await axiosInstance.get(
    getAPIURL(`/User/ByObjectId/${encodeURIComponent(options.objectId.trim())}`),
  );
  const existingId =
    existing?.data?.user?.id ?? existing?.data?.User?.id ?? null;
  if (!existingId) {
    throw new Error("User profile not found");
  }
  formData.set("id", String(existingId));
  await axiosInstance.put(getAPIURL("/User/Update"), formData, config);
}

export function formSaveIndicatesPaymentComplete(variables: unknown): boolean {
  if (variables instanceof FormData) {
    return variables.get("hasPaid") === "1";
  }
  if (variables && typeof variables === "object" && "hasPaid" in variables) {
    const hasPaid = (variables as { hasPaid?: number | string }).hasPaid;
    return hasPaid === 1 || hasPaid === "1";
  }
  return false;
}
